// Gerätespeicher für Anzeige-Daten (Sofortanzeige beim Kaltstart, 14.10.2026).
// Nach einem Kaltstart zeigt die App den letzten Stand sofort und lädt im Hintergrund neu
// (stale-while-revalidate). Bewusst klein und ohne Library (kein Query-Persister im Projekt).
//
// Regeln:
// - Je Nutzer-ID ein eigener Eintrag, ein anderer Nutzer sieht nie fremde Daten.
// - Versionsschlüssel: ändert sich das Datenformat, VERSION hochzählen, alte Stände gelten nicht.
// - Höchstens 24 h alt, ältere Einträge werden ignoriert und beim nächsten Schreiben entfernt.
// - Nur Anzeige-Daten. Keine Tokens. Signierte URLs nur mit kürzerem Höchstalter als ihre Gültigkeit.
// - Beim Abmelden alles löschen (AuthContext).
// Synchron über localStorage, damit React Query die Daten schon beim ersten Rendern hat
// (Capacitor Preferences wäre asynchron und käme erst nach dem ersten Bild).

const PREFIX = "cc-anzeige:";
const VERSION = 1;
export const ANZEIGE_MAX_ALTER_MS = 24 * 60 * 60 * 1000;

type Eintrag = { t: number; d: unknown };
type Speicher = { v: number; e: Record<string, Eintrag> };

function speicherLesen(userId: string): Speicher | null {
  try {
    const roh = localStorage.getItem(PREFIX + userId);
    if (!roh) return null;
    const s = JSON.parse(roh) as Speicher;
    if (!s || s.v !== VERSION || typeof s.e !== "object" || s.e === null) return null;
    return s;
  } catch {
    return null;
  }
}

/** Liefert den gespeicherten Stand, wenn er jünger als `maxAlterMs` (höchstens 24 h) ist. */
export function anzeigeLesen<T>(userId: string | undefined, schluessel: string, maxAlterMs = ANZEIGE_MAX_ALTER_MS): { daten: T; gespeichert: number } | null {
  if (!userId) return null;
  const e = speicherLesen(userId)?.e[schluessel];
  if (!e || typeof e.t !== "number") return null;
  const alter = Date.now() - e.t;
  if (alter < 0 || alter > Math.min(maxAlterMs, ANZEIGE_MAX_ALTER_MS)) return null;
  return { daten: e.d as T, gespeichert: e.t };
}

/** Speichert (oder entfernt mit `daten === undefined`) einen Eintrag; räumt dabei Abgelaufenes weg. */
export function anzeigeSchreiben(userId: string | undefined, schluessel: string, daten: unknown, gespeichert = Date.now()): void {
  if (!userId) return;
  try {
    const s: Speicher = speicherLesen(userId) ?? { v: VERSION, e: {} };
    const jetzt = Date.now();
    for (const [k, e] of Object.entries(s.e)) {
      if (!e || jetzt - e.t > ANZEIGE_MAX_ALTER_MS) delete s.e[k];
    }
    if (daten === undefined) delete s.e[schluessel];
    else s.e[schluessel] = { t: gespeichert, d: daten };
    localStorage.setItem(PREFIX + userId, JSON.stringify(s));
  } catch {
    /* Speicher voll oder gesperrt (privates Fenster): dann eben ohne Sofortanzeige */
  }
}

/**
 * Darf eine Foto-URL in den Gerätespeicher? Ohne Signatur (öffentliche Datei) ja. Signierte
 * Supabase-URLs (`?token=<JWT>`) nur, wenn die Signatur noch länger gilt als `mindestensMs`
 * (Standard: das Höchstalter des Speichers). Nicht lesbares Token: nein (dann Platzhalter).
 */
export function urlDarfInSpeicher(url: string | null | undefined, mindestensMs = ANZEIGE_MAX_ALTER_MS): boolean {
  if (!url) return false;
  const m = /[?&]token=([^&#]+)/.exec(url);
  if (!m) return true;
  try {
    const teil = decodeURIComponent(m[1]).split(".")[1];
    if (!teil) return false;
    const json = atob(teil.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(teil.length / 4) * 4, "="));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" && exp * 1000 - Date.now() > mindestensMs;
  } catch {
    return false;
  }
}

/**
 * Weitere kundenbezogene Gerätespeicher, die beim Abmelden weg müssen (Datenschutz, 14.10.2026).
 * Bewusst als Text hier statt Import, damit der Startbundle nicht wächst.
 * - `cc.photoUrl.v1`: signierte Foto-URLs (Hunde-/Walkfotos, 7 Tage gültig), siehe photoUrl.ts.
 * - `cc_pending_booking_state`: halbfertige Buchung (Telefon, Abhol-/Bringadresse), siehe bookingStateStorage.ts.
 * Per-Gerät-Komfort (Hinweis-Merker, Cookie-Einwilligung, Face-ID-Merker, Team-Modus) bleibt.
 */
const KUNDEN_SCHLUESSEL = ["cc.photoUrl.v1", "cc_pending_booking_state"];
/** Event für Module mit Speicher-Cache im Arbeitsspeicher (photoUrl.ts). */
export const ABGEMELDET_EVENT = "cc:abgemeldet";

/** Beim Abmelden: alle gespeicherten Anzeige-Daten aller Nutzer und kundenbezogene Speicher löschen. */
export function anzeigeCacheLeeren(): void {
  try {
    const weg: string[] = [...KUNDEN_SCHLUESSEL];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX)) weg.push(k);
    }
    for (const k of weg) localStorage.removeItem(k);
  } catch {
    /* egal */
  }
  try {
    window.dispatchEvent(new Event(ABGEMELDET_EVENT));
  } catch {
    /* egal */
  }
}
